/**
 * Profile360 Response Processor
 * Transforms API responses into formats suitable for visualization components
 */

/**
 * Response Processor class for Profile360
 */
class ResponseProcessor {
  constructor() {
    this.nodeIdCounter = 0;
    this.linkIdCounter = 0;
  }

  /**
   * Generate unique node ID
   * @returns {string} Unique node ID
   */
  generateNodeId() {
    return `node_${++this.nodeIdCounter}`;
  }

  /**
   * Generate unique link ID
   * @returns {string} Unique link ID
   */
  generateLinkId() {
    return `link_${++this.linkIdCounter}`;
  }

  /**
   * Get node count from processed data
   * @param {Object} processedData - Processed data
   * @returns {number} Node count
   */
  getNodeCount(processedData) {
    if (processedData.type === 'graph') {
      return processedData.nodes?.length || 0;
    }
    if (processedData.type === 'mixed') {
      return processedData.graph?.nodes?.length || 0;
    }
    return 0;
  }

  /**
   * Get record count from processed data
   * @param {Object} processedData - Processed data
   * @returns {number} Record count
   */
  getRecordCount(processedData) {
    if (processedData.type === 'table') {
      return processedData.records?.length || 0;
    }
    if (processedData.type === 'mixed') {
      return processedData.table?.records?.length || 0;
    }
    if (processedData.type === 'insights') {
      return Array.isArray(processedData.items) ? processedData.items.length : 1;
    }
    return 0;
  }

  /**
   * Process API response based on response type
   * @param {Object} apiResponse - Raw API response
   * @returns {Object} Processed response for visualization
   */
  processResponse(apiResponse) {

    if (!apiResponse.success) {
      return {
        success: false,
        error: apiResponse.error,
        data: null
      };
    }

    const { data, metadata } = apiResponse;
    const responseType = metadata?.response_type || 'table';

    try {
      let processedData;

      switch (responseType) {
        case 'graph':
          processedData = this.processGraphResponse(data);
          break;
        case 'table':
          processedData = this.processTableResponse(data);
          break;
        case 'mixed':
          processedData = this.processMixedResponse(data);
          break;
        case 'insights':
          processedData = this.processInsightsResponse(data);
          break;
        default:
          // Auto-detect based on data structure
          processedData = this.autoDetectAndProcess(data);
      }

      return {
        success: true,
        data: processedData,
        metadata: {
          ...metadata,
          processed_type: processedData.type,
          node_count: this.getNodeCount(processedData),
          record_count: this.getRecordCount(processedData),
          has_graph_data: processedData.type === 'mixed' ? !!processedData.graph : processedData.type === 'graph',
          has_table_data: processedData.type === 'mixed' ? !!processedData.table : processedData.type === 'table'
        }
      };

    } catch (error) {
      return {
        success: false,
        error: `Response processing failed: ${error.message}`,
        data: null
      };
    }
  }

  /**
   * Process graph response data
   * @param {Object} data - Raw graph data
   * @returns {Object} Processed graph data
   */
  processGraphResponse(data) {
    // Handle your API's data structure: { graph_data: { nodes: [...], links: [...] } }
    let graphData = data;
    if (data.graph_data) {
      graphData = data.graph_data;
    }

    // Handle different graph response formats
    if (graphData.nodes && Array.isArray(graphData.nodes)) {
      // Already in graph format (your format) - use as-is without normalization
      const result = {
        type: 'graph',
        nodes: graphData.nodes, // Use raw nodes directly
        links: graphData.links || [] // Use raw links directly
      };
      return result;
    }

    if (data.records) {
      // Neo4j-style records
      return this.convertRecordsToGraph(data.records);
    }

    if (Array.isArray(data)) {
      // Array of records
      return this.convertRecordsToGraph(data);
    }

    // Try to extract graph data from various formats
    return this.extractGraphFromData(data);
  }

  /**
   * Process mixed response data (contains both graph_data and table_data)
   * @param {Object} data - Raw mixed data
   * @returns {Object} Processed mixed data
   */
  processMixedResponse(data) {
    const result = {
      type: 'mixed',
      graph: null,
      table: null
    };

    // Try multiple possible graph data locations
    let graphData = null;
    if (data.graph_data) {
      graphData = data.graph_data;
    } else if (data.nodes || data.links) {
      // Direct graph format
      graphData = data;
    } else if (data.records && this.looksLikeGraphData(data)) {
      // Neo4j records that can be converted to graph
      graphData = data;
    }

    // Process graph data if found
    if (graphData) {
      try {
        // Special handling for your Neo4j format where nodes are complete but links might be empty
        if (graphData.nodes && Array.isArray(graphData.nodes) && graphData.nodes.length > 0) {
          result.graph = {
            type: 'graph',
            nodes: this.normalizeNodes(graphData.nodes),
            links: this.normalizeLinks(graphData.links || [])
          };
        } else {
          result.graph = this.processGraphResponse(graphData);
        }
      } catch (error) {
        result.graph = { type: 'graph', nodes: [], links: [] };
      }
    }

    // Try multiple possible table data locations
    let tableData = null;
    if (data.table_data) {
      tableData = data.table_data;
    } else if (Array.isArray(data)) {
      // Direct array format
      tableData = data;
    } else if (data.records && Array.isArray(data.records)) {
      // Records array
      tableData = data.records;
    } else if (data.results && Array.isArray(data.results)) {
      // Results array
      tableData = data.results;
    }

    // Process table data if found
    if (tableData) {
      try {
        result.table = this.processTableResponse(tableData);
      } catch (error) {
        result.table = { type: 'table', records: [] };
      }
    }

    // If no specific data found, try to auto-detect
    if (!result.graph && !result.table) {
      const autoDetected = this.autoDetectAndProcess(data);
      if (autoDetected.type === 'graph') {
        result.graph = autoDetected;
      } else {
        result.table = autoDetected;
      }
    }

    return result;
  }

  /**
   * Process table response data
   * @param {Object} data - Raw table data
   * @returns {Object} Processed table data
   */
  processTableResponse(data) {
    if (Array.isArray(data)) {
      return {
        type: 'table',
        records: this.normalizeTableRecords(data)
      };
    }

    if (data.records && Array.isArray(data.records)) {
      return {
        type: 'table',
        records: this.normalizeTableRecords(data.records)
      };
    }

    if (data.results && Array.isArray(data.results)) {
      return {
        type: 'table',
        records: this.normalizeTableRecords(data.results)
      };
    }

    // Convert single object to array
    if (typeof data === 'object' && data !== null) {
      return {
        type: 'table',
        records: [this.flattenObject(data)]
      };
    }

    return {
      type: 'table',
      records: []
    };
  }

  /**
   * Auto-detect data type and process accordingly
   * @param {Object} data - Raw data
   * @returns {Object} Processed data
   */
  autoDetectAndProcess(data) {
    // Check if it looks like graph data
    if (this.looksLikeGraphData(data)) {
      return this.processGraphResponse(data);
    }

    // Default to table processing
    return this.processTableResponse(data);
  }

  /**
   * Check if data looks like graph data
   * @param {Object} data - Data to check
   * @returns {boolean} True if looks like graph data
   */
  looksLikeGraphData(data) {
    // Has nodes and links
    if (data.nodes && data.links) return true;

    // Has graph_data property
    if (data.graph_data) return true;

    // Has records with relationships
    if (data.records && Array.isArray(data.records)) {
      return data.records.some(record =>
        record.relationships || record.paths || record._fields ||
        // Check if record contains node-like objects
        (typeof record === 'object' && (record.labels || record.type || record.properties))
      );
    }

    // Check if it's a Cypher query result with nodes/relationships
    if (Array.isArray(data)) {
      return data.some(item =>
        item && typeof item === 'object' &&
        (item.labels || item.type || item.relationships || item._fields)
      );
    }

    return false;
  }

  /**
   * Convert Neo4j records to graph format
   * @param {Array} records - Neo4j records
   * @returns {Object} Graph data
   */
  convertRecordsToGraph(records) {
    const nodes = new Map();
    const links = [];

    records.forEach(record => {
      // Handle different record formats
      if (record._fields) {
        // Neo4j driver format
        this.processNeo4jRecord(record, nodes, links);
      } else if (record.nodes && record.relationships) {
        // Graph result format
        this.processGraphRecord(record, nodes, links);
      } else {
        // Generic record - try to extract entities
        this.processGenericRecord(record, nodes, links);
      }
    });

    return {
      type: 'graph',
      nodes: Array.from(nodes.values()),
      links: links
    };
  }

  /**
   * Process Neo4j driver record
   * @param {Object} record - Neo4j record
   * @param {Map} nodes - Nodes map
   * @param {Array} links - Links array
   */
  processNeo4jRecord(record, nodes, links) {
    record._fields.forEach(field => {
      if (field && field.labels) {
        // Node
        const nodeId = field.identity?.toString() || this.generateNodeId();
        if (!nodes.has(nodeId)) {
          nodes.set(nodeId, {
            id: nodeId,
            name: field.properties?.name || field.properties?.full_name || `Node ${nodeId}`,
            labels: field.labels,
            properties: field.properties || {},
            type: field.labels?.[0] || 'Unknown'
          });
        }
      } else if (field && field.type) {
        // Relationship
        const linkId = this.generateLinkId();
        links.push({
          id: linkId,
          source: field.start?.toString(),
          target: field.end?.toString(),
          type: field.type,
          properties: field.properties || {}
        });
      }
    });
  }

  /**
   * Process graph record
   * @param {Object} record - Graph record
   * @param {Map} nodes - Nodes map
   * @param {Array} links - Links array
   */
  processGraphRecord(record, nodes, links) {
    // Add nodes
    if (record.nodes) {
      record.nodes.forEach(node => {
        const nodeId = node.id || this.generateNodeId();
        if (!nodes.has(nodeId)) {
          nodes.set(nodeId, this.normalizeNode(node));
        }
      });
    }

    // Add relationships
    if (record.relationships) {
      record.relationships.forEach(rel => {
        links.push(this.normalizeLink(rel));
      });
    }
  }

  /**
   * Process generic record
   * @param {Object} record - Generic record
   * @param {Map} nodes - Nodes map
   * @param {Array} links - Links array
   */
  processGenericRecord(record, nodes, links) {
    // Try to identify entities in the record
    Object.keys(record).forEach(key => {
      const value = record[key];

      if (typeof value === 'object' && value !== null) {
        if (value.id || value.name) {
          // Looks like an entity
          const nodeId = value.id || this.generateNodeId();
          if (!nodes.has(nodeId)) {
            nodes.set(nodeId, {
              id: nodeId,
              name: value.name || value.title || key,
              type: key,
              properties: value
            });
          }
        }
      }
    });
  }

  /**
   * Normalize nodes array
   * @param {Array} nodes - Raw nodes
   * @returns {Array} Normalized nodes
   */
  normalizeNodes(nodes) {
    // Handle null/undefined or non-array input
    if (!nodes || !Array.isArray(nodes)) {
      return [];
    }

    return nodes
      .filter(node => node !== null && node !== undefined) // Filter out null/undefined nodes
      .map(node => this.normalizeNode(node));
  }

  /**
   * Normalize single node
   * @param {Object} node - Raw node
   * @returns {Object} Normalized node
   */
  normalizeNode(node) {
    // Handle null/undefined nodes
    if (!node || typeof node !== 'object') {
      return {
        id: this.generateNodeId(),
        name: 'Unknown Node',
        type: 'Unknown',
        labels: ['Unknown'],
        properties: {}
      };
    }

    // Ensure labels is always an array
    let labels = [];
    if (node.labels && Array.isArray(node.labels)) {
      labels = node.labels.filter(label => label && typeof label === 'string');
    } else if (node.type && typeof node.type === 'string') {
      labels = [node.type];
    }

    // Fallback to 'Unknown' if no valid labels found
    if (labels.length === 0) {
      labels = ['Unknown'];
    }

    const nodeId = node.id || this.generateNodeId();
    const nodeName = node.name || node.title || node.label || node.full_name || `Node ${nodeId}`;
    const nodeType = node.type || labels[0] || 'Unknown';

    const normalizedNode = {
      ...node,
      // Ensure these critical properties are set correctly
      id: nodeId,
      name: nodeName,
      type: nodeType,
      labels: labels,
      properties: node.properties || {}
    };

    return normalizedNode;
  }

  /**
   * Normalize links array
   * @param {Array} links - Raw links
   * @returns {Array} Normalized links
   */
  normalizeLinks(links) {
    // Handle null/undefined or non-array input
    if (!links || !Array.isArray(links)) {
      return [];
    }

    return links
      .filter(link => link !== null && link !== undefined) // Filter out null/undefined links
      .map(link => this.normalizeLink(link));
  }

  /**
   * Normalize single link
   * @param {Object} link - Raw link
   * @returns {Object} Normalized link
   */
  normalizeLink(link) {
    // Handle null/undefined links
    if (!link || typeof link !== 'object') {
      return {
        id: this.generateLinkId(),
        source: 'unknown',
        target: 'unknown',
        type: 'UNKNOWN',
        properties: {}
      };
    }

    const linkId = link.id || this.generateLinkId();
    const source = link.source || link.start || link.from || 'unknown';
    const target = link.target || link.end || link.to || 'unknown';
    const type = link.type || link.relationship || 'CONNECTED';

    return {
      ...link,
      // Ensure these critical properties are set correctly
      id: linkId,
      source: source,
      target: target,
      type: type,
      properties: link.properties || {}
    };
  }

  /**
   * Normalize table records
   * @param {Array} records - Raw records
   * @returns {Array} Normalized records
   */
  normalizeTableRecords(records) {
    return records.map(record => this.flattenObject(record));
  }

  /**
   * Flatten nested object for table display
   * @param {Object} obj - Object to flatten
   * @param {string} prefix - Key prefix
   * @returns {Object} Flattened object
   */
  flattenObject(obj, prefix = '') {
    const flattened = {};

    Object.keys(obj).forEach(key => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(flattened, this.flattenObject(value, newKey));
      } else {
        flattened[newKey] = value;
      }
    });

    return flattened;
  }

  /**
   * Extract graph data from various formats
   * @param {Object} data - Raw data
   * @returns {Object} Graph data
   */
  extractGraphFromData(data) {
    const nodes = new Map();
    const links = [];

    // Try to extract entities and relationships
    this.extractEntitiesFromObject(data, nodes, links);

    return {
      type: 'graph',
      nodes: Array.from(nodes.values()),
      links: links
    };
  }

  /**
   * Extract entities from object recursively
   * @param {Object} obj - Object to process
   * @param {Map} nodes - Nodes map
   * @param {Array} links - Links array
   * @param {string} parentId - Parent node ID
   */
  extractEntitiesFromObject(obj, nodes, links, parentId = null) {
    if (!obj || typeof obj !== 'object') return;

    Object.keys(obj).forEach(key => {
      const value = obj[key];

      if (Array.isArray(value)) {
        value.forEach(item => {
          if (typeof item === 'object' && item !== null) {
            this.extractEntitiesFromObject(item, nodes, links, parentId);
          }
        });
      } else if (typeof value === 'object' && value !== null) {
        if (value.id || value.name) {
          // This looks like an entity
          const nodeId = value.id || this.generateNodeId();

          if (!nodes.has(nodeId)) {
            nodes.set(nodeId, {
              id: nodeId,
              name: value.name || value.title || key,
              type: key,
              properties: value
            });
          }
          // Create link to parent if exists
          if (parentId) {
            links.push({
              id: this.generateLinkId(),
              source: parentId,
              target: nodeId,
              type: 'CONTAINS'
            });
          }

          // Recursively process this entity
          this.extractEntitiesFromObject(value, nodes, links, nodeId);
        } else {
          // Regular nested object
          this.extractEntitiesFromObject(value, nodes, links, parentId);
        }
      }
    });
  }
  /**
   * Process user insights response data
   * @param {Object|Array} data - Raw insights data from /api/users/{email}/insights
   * @returns {Object} Processed insights data for dedicated component
   */
  processInsightsResponse(data) {
    const items = Array.isArray(data)
      ? data
      : (Array.isArray((data && data.policies)) ? data.policies : [data]).filter(Boolean);

    return {
      type: 'insights',
      items
    };
  }


}

// Create singleton instance
const responseProcessor = new ResponseProcessor();

export default responseProcessor;
